import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import fleetApi from "@/lib/fleetApi";
import { useAuth } from "@/context/AuthContext";
import { Panel, PageHeader, StatusPill, Loading } from "@/components/portal/widgets";
import IncidentStatusDialog from "@/components/portal/IncidentStatusDialog";
import { Info, Pencil } from "lucide-react";

const EDIT_ROLES = ["owner", "supervisor", "dispatcher"];

export default function IncidentsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const canEdit = EDIT_ROLES.includes(user?.role);
  const [tab, setTab] = useState(searchParams.get("tab") === "compliance" ? "compliance" : "incidents");
  const [incidents, setIncidents] = useState(null);
  const [compliance, setCompliance] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    Promise.all([fleetApi.get("/incidents"), fleetApi.get("/compliance")])
      .then(([i, c]) => { setIncidents(i.data); setCompliance(c.data?.records || []); }).catch(() => setError(true));
  }, []);

  const applyStatus = (id, patch) => setIncidents((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  if (error) return <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-5 py-8 text-center text-sm text-red-300" data-testid="incidents-error">Couldn't load incidents from the RideStamp backend. Please refresh to try again.</div>;
  if (!incidents) return <Loading />;

  return (
    <div data-testid="incidents-page">
      <PageHeader title="Incidents & Compliance" subtitle={`${incidents.length} incidents · ${compliance.length} compliance records (live)`} />

      <div className="mb-5 flex gap-2 border-b border-slate-800">
        {[["incidents", "Incidents"], ["compliance", "Compliance"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`incidents-tab-${k}`}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === k ? "border-blue-500 text-blue-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>{label}</button>
        ))}
      </div>

      {tab === "incidents" && (
        <>
          <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-slate-800 bg-blue-500/5 px-4 py-2.5 text-xs text-slate-400" data-testid="incidents-note">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" />
            <span>{canEdit
              ? "Live incident records. Use Update to change a status — changes are written to the shared RideStamp backend and audited."
              : "Live, read-only incident records. Status updates require a dispatcher, supervisor or owner role."}</span>
          </div>
          <Panel testid="incidents-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-800 text-left font-mono-data text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3">Type / Rider</th><th className="px-3 py-3">Driver</th><th className="px-3 py-3 max-w-md">Description</th><th className="px-3 py-3">Status</th>
                  {canEdit && <th className="px-3 py-3">Action</th>}
                </tr></thead>
                <tbody className="divide-y divide-slate-800">
                  {incidents.map((i) => (
                    <tr key={i.id} className="hover:bg-slate-800/30" data-testid={`incident-row-${i.id}`}>
                      <td className="px-5 py-3"><div className="font-semibold text-white">{i.type}</div><div className="text-[11px] text-slate-500">{i.rider_name}</div></td>
                      <td className="px-3 py-3 text-slate-300">{i.driver_name}</td>
                      <td className="px-3 py-3 max-w-md text-xs text-slate-400"><div className="line-clamp-2">{i.description}</div></td>
                      <td className="px-3 py-3"><StatusPill value={i.status} /></td>
                      {canEdit && (
                        <td className="px-3 py-3">
                          <button onClick={() => setEditing(i)} data-testid={`incident-edit-${i.id}`}
                            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800/60 px-2 py-1 text-[11px] font-medium text-blue-300 transition-colors hover:bg-slate-700">
                            <Pencil className="h-3 w-3" /> Update
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}

      {tab === "compliance" && (
        <Panel testid="compliance-table">
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#111827]"><tr className="border-b border-slate-800 text-left font-mono-data text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Entity</th><th className="px-3 py-3">Document</th><th className="px-3 py-3">Expiration</th><th className="px-3 py-3">Days Left</th><th className="px-3 py-3">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-800">
                {compliance.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-800/30" data-testid={`compliance-row-${c.id}`}>
                    <td className="px-5 py-3"><span className="text-white">{c.entity_name}</span> <span className="font-mono-data text-[11px] text-slate-500">({c.entity_type})</span></td>
                    <td className="px-3 py-3 text-slate-300">{c.doc_type}</td>
                    <td className="px-3 py-3 font-mono-data text-xs text-slate-400">{(c.expiration_date || "").slice(0, 10)}</td>
                    <td className="px-3 py-3 font-mono-data text-xs text-slate-400">{c.days_left}d</td>
                    <td className="px-3 py-3"><StatusPill value={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <IncidentStatusDialog incident={editing} open={!!editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }} onUpdated={applyStatus} />
    </div>
  );
}
